import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import useAppToast from '@/hooks/useAppToast';
import {
  CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE,
  isClinicInformationClinicIdNotExistsApiError,
} from '@/pages/admin/site-admin-clinic-info/applyClinicInformationApiErrors';
import { isAxiosError } from 'axios';
import { flattenClinicCreateData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { apiClient } from '@/shared/lib/axios';
import { getApiErrorMessage } from '@/utils/apiError';

type FlattenedClinicData = ReturnType<typeof flattenClinicCreateData>;

const updateClinicInfo = async (siteType: string, id: string, data: FlattenedClinicData) => {
  const response = await apiClient.put(`/admin/${siteType}/clinic-informations/${id}`, data);
  return response.data;
};

export const useAdminClinicInfoUpdate = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FlattenedClinicData }) =>
      updateClinicInfo(currentSite, id, data),
    meta: { suppressNotFoundToast: true },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['clinic-info-detail', variables.id, currentSite],
      });
      toast.success('更新しました。');
    },
    onError: error => {
      if (isClinicInformationClinicIdNotExistsApiError(error)) {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'clinics'] });
        toast.error(CLINIC_INFORMATION_CLINIC_ID_NOT_EXISTS_API_MESSAGE);
        return;
      }
      // 422 validation errors: no toast, field errors are mapped by applyClinicInformationApiValidationErrors
      if (isAxiosError(error) && error.response?.status === 422) {
        return;
      }
      const msg = getApiErrorMessage(error, 'エラーが発生しました。');
      toast.error(msg);
    },
  });
};
